/**
 * A aba Corretivas: a lista completa, com o formulário de criar e editar.
 *
 * Papel: reproduz o painel `#panel-corretivas` do SM (l. 571–609) e
 * `renderCorretivas` (l. 974–1009): busca em área, descrição e responsável,
 * multiseleções Status e Área — Status antes de Área, e sem responsável —,
 * período sobre a atualização, oito colunas, sete ordenáveis, e "Editar" na
 * linha.
 *
 * Como usar: a página passa as corretivas da casa, sem filtro, a casa para o
 * formulário gravar, e `podeEditar`. Quem só tem `ver` não vê "+ Novo registro"
 * nem "Editar", e a coluna de ações continua no lugar, vazia, para as outras
 * manterem a largura. A coluna de ações não tem largura própria: fica com os
 * 10 % que as outras deixam, como na origem, onde o `width:72px` está no `td` e
 * o layout fixo só lê o `th`. Excluir fica dentro do formulário.
 */
import { type Corretiva } from '@concierge/shared';
import { useState } from 'react';

import {
  CabecalhoOrdenavel,
  type ColunaOrdenavel,
  FiltroDePeriodo,
  MultiSelecao,
  type OpcaoDeSelecao,
  dentroDoPeriodo,
  useMultiSelecao,
  useOrdenacao,
} from '@/shared/embutido';
import { cn } from '@/shared/lib/utils';
import {
  distintosOrdenados,
  formatarCustoDaTabela,
  formatarDataDaTabela,
  opcoesDeArea,
  passaNaBusca,
  situacaoDaLinhaVazia,
  valorDaArea,
} from '../regras';
import {
  ORDEM_DOS_STATUS_CORRETIVA,
  ROTULOS_STATUS_CORRETIVA,
  TOM_DO_SELO_CORRETIVA,
} from '../rotulos';
import { type SituacaoDaLista } from '../types';
import { FormularioCorretiva } from './formulario-corretiva';
import { BarraDeFiltros, Espacador } from './kit/barra-de-filtros';
import { Botao } from './kit/botao';
import { BotaoDaBarra } from './kit/botao-da-barra';
import {
  BuscaDaBarra,
  CLASSE_DA_DATA_DA_BARRA,
  CLASSE_DO_ROTULO_DA_BARRA,
} from './kit/campos-da-barra';
import { FaixaDoCartao } from './kit/faixa-do-cartao';
import { Selo } from './kit/selo';
import {
  CLASSE_DO_CABECALHO_ORDENAVEL,
  Celula,
  CelulaDeCabecalho,
  LinhaDaTabela,
  LinhaVazia,
  Tabela,
} from './kit/tabela';

/** Como cada coluna ordena; o status pelo rótulo que aparece no selo. */
const COLUNAS: Readonly<Record<string, ColunaOrdenavel<Corretiva>>> = {
  area: { tipo: 'texto', valor: (corretiva) => corretiva.area },
  descricao: { tipo: 'texto', valor: (corretiva) => corretiva.descricao },
  status: { tipo: 'texto', valor: (corretiva) => ROTULOS_STATUS_CORRETIVA[corretiva.status] },
  responsavel: { tipo: 'texto', valor: (corretiva) => corretiva.responsavel },
  atualizacao: { tipo: 'data', valor: (corretiva) => corretiva.atualizacao },
  custo: { tipo: 'numero', valor: (corretiva) => corretiva.custo },
  observacao: { tipo: 'texto', valor: (corretiva) => corretiva.observacao },
};

/** Os cabeçalhos ordenáveis, na ordem e com a largura da origem (l. 605–606). */
const CABECALHOS = [
  { chave: 'area', rotulo: 'ÁREA', largura: 'w-[9%]' },
  { chave: 'descricao', rotulo: 'DESCRIÇÃO / PENDÊNCIA', largura: 'w-[22%]' },
  { chave: 'status', rotulo: 'STATUS', largura: 'w-[14%]' },
  { chave: 'responsavel', rotulo: 'RESPONSÁVEL', largura: 'w-[13%]' },
  { chave: 'atualizacao', rotulo: 'ÚLT. ATUALIZAÇÃO', largura: 'w-[9%]' },
  { chave: 'custo', rotulo: 'CUSTO (R$)', largura: 'w-[8%]' },
  { chave: 'observacao', rotulo: 'OBSERVAÇÕES', largura: 'w-[15%]' },
] as const;

/** As opções de status, sem percentual (l. 577–579). */
const OPCOES_DE_STATUS: readonly OpcaoDeSelecao[] = ORDEM_DOS_STATUS_CORRETIVA.map(
  (status) => ({
    valor: status,
    rotulo: ROTULOS_STATUS_CORRETIVA[status],
  }),
);

/** Os valores das opções de status, para o `useMultiSelecao`. */
const VALORES_DE_STATUS: readonly string[] = ORDEM_DOS_STATUS_CORRETIVA;

/** A caixa do formulário: fechada, ou aberta criando ou editando. */
type CaixaAberta = { aberta: false } | { aberta: true; corretiva: Corretiva | null };

interface AbaCorretivasProps {
  /** As corretivas da casa, sem filtro. */
  corretivas: readonly Corretiva[];
  situacao: SituacaoDaLista;
  /** Relê a lista depois de uma falha. */
  onTentarDeNovo: () => void;
  /** A casa em que o formulário grava. */
  residenciaId: string;
  /** `false` para quem só tem `ver`. */
  podeEditar: boolean;
}

/**
 * A aba Corretivas.
 *
 * @param corretivas - As corretivas da casa.
 * @param situacao - A situação da consulta.
 * @param onTentarDeNovo - Relê a lista.
 * @param residenciaId - A casa do formulário.
 * @param podeEditar - Se mostra "+ Novo registro" e "Editar".
 * @returns A faixa, a barra de filtros, a tabela e, aberta, a caixa.
 */
export function AbaCorretivas({
  corretivas,
  situacao,
  onTentarDeNovo,
  residenciaId,
  podeEditar,
}: AbaCorretivasProps) {
  const [busca, setBusca] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [caixa, setCaixa] = useState<CaixaAberta>({ aberta: false });
  const areas = opcoesDeArea(corretivas);
  const status = useMultiSelecao(VALORES_DE_STATUS);
  const area = useMultiSelecao(areas);

  const filtradas = corretivas.filter(
    (corretiva) =>
      status.marcados.has(corretiva.status) &&
      area.marcados.has(valorDaArea(corretiva)) &&
      dentroDoPeriodo(corretiva.atualizacao, de, ate) &&
      passaNaBusca(busca, [corretiva.area, corretiva.descricao, corretiva.responsavel]),
  );
  const ordenacao = useOrdenacao(filtradas, COLUNAS);
  const vazia = situacaoDaLinhaVazia(situacao, filtradas.length);

  return (
    <div>
      <FaixaDoCartao>{'VILLA PRADO  ·  MANUTENÇÕES CORRETIVAS'}</FaixaDoCartao>
      <BarraDeFiltros>
        <BuscaDaBarra
          placeholder="Buscar descrição, área, responsável..."
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
        />
        <MultiSelecao
          prefixo="Status"
          opcoes={OPCOES_DE_STATUS}
          marcados={status.marcados}
          onMudar={status.definir}
        />
        <MultiSelecao
          prefixo="Área"
          opcoes={areas.map((valor) => ({ valor, rotulo: valor }))}
          marcados={area.marcados}
          onMudar={area.definir}
        />
        <FiltroDePeriodo
          rotulo="Atualização"
          de={de}
          ate={ate}
          onMudarDe={setDe}
          onMudarAte={setAte}
          classeDoRotulo={CLASSE_DO_ROTULO_DA_BARRA}
          classeDoCampo={CLASSE_DA_DATA_DA_BARRA}
        />
        <Espacador />
        {podeEditar && (
          <BotaoDaBarra onClick={() => setCaixa({ aberta: true, corretiva: null })}>
            + Novo registro
          </BotaoDaBarra>
        )}
      </BarraDeFiltros>

      <Tabela>
        <thead>
          <tr>
            {CABECALHOS.map(({ chave, rotulo, largura }) => (
              <CabecalhoOrdenavel
                key={chave}
                seta={ordenacao.seta(chave)}
                onAlternar={() => ordenacao.alternar(chave)}
                className={cn(CLASSE_DO_CABECALHO_ORDENAVEL, largura)}
              >
                {rotulo}
              </CabecalhoOrdenavel>
            ))}
            <CelulaDeCabecalho>
              <span className="sr-only">Ações</span>
            </CelulaDeCabecalho>
          </tr>
        </thead>
        <tbody>
          {vazia !== null ? (
            <LinhaVazia colunas={8} situacao={vazia} onTentarDeNovo={onTentarDeNovo} />
          ) : (
            ordenacao.linhas.map((corretiva) => (
              <LinhaDaTabela key={corretiva.id}>
                <Celula tipo="titulo">{corretiva.area}</Celula>
                <Celula tipo="quebra">{corretiva.descricao}</Celula>
                <Celula>
                  <Selo tom={TOM_DO_SELO_CORRETIVA[corretiva.status]}>
                    {ROTULOS_STATUS_CORRETIVA[corretiva.status]}
                  </Selo>
                </Celula>
                <Celula tipo="fraco">{corretiva.responsavel}</Celula>
                <Celula tipo="fraco">{formatarDataDaTabela(corretiva.atualizacao)}</Celula>
                <Celula tipo="numero">{formatarCustoDaTabela(corretiva.custo)}</Celula>
                <Celula tipo="quebra">{corretiva.observacao}</Celula>
                <Celula tipo="acoes">
                  {podeEditar && (
                    <Botao
                      variante="fantasma"
                      pequeno
                      onClick={() => setCaixa({ aberta: true, corretiva })}
                    >
                      Editar
                    </Botao>
                  )}
                </Celula>
              </LinhaDaTabela>
            ))
          )}
        </tbody>
      </Tabela>

      {caixa.aberta && (
        <FormularioCorretiva
          key={caixa.corretiva?.id ?? 'nova'}
          corretiva={caixa.corretiva}
          residenciaId={residenciaId}
          areas={distintosOrdenados(corretivas.map((corretiva) => corretiva.area))}
          onFechar={() => setCaixa({ aberta: false })}
        />
      )}
    </div>
  );
}
